import type { Geometry } from 'geojson';
import { html, nothing, render } from 'lit-html';
import { ref } from 'lit-html/directives/ref.js';
import type { EventTypes } from 'ol/Observable';

import closeIcon from '@disy/cadenza-icons/close.svg';
import maximizeIcon from '@disy/cadenza-icons/maximize.svg';
import minimizeIcon from '@disy/cadenza-icons/minimize.svg';
import { backendSingleExecution } from '@disy/lib.backend/single-execution';
import { isChromiumBrowser, isSafari } from '@disy/lib/browser';
import { requireNonNullable } from '@disy/lib/custom-error';
import { focusFirst, on } from '@disy/lib/event-util';
import { isEqual } from '@disy/lib/is-equal';
import { supportsTouch } from '@disy/lib/media';
import { px } from '@disy/lib/px';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { throttle } from '@disy/lib/throttle';
import { createAnchorButton, createIconButton } from '@disy/ui/button';
import { normalizeContent } from '@disy/ui/content';
import { icon } from '@disy/ui/icon';
import { type MenuItem, type MenuItemGroup, createMenuContent } from '@disy/ui/menu';
import { getPopupParent } from '@disy/ui/popup';
import { getSetting } from '@disy/workbook.lib/settings';

import type { Action } from 'cadenza/action';
import type { MenuAction } from 'cadenza/actions/menu-action';
import { isMenuItemGroup, unpackMenuItemGroups } from 'cadenza/actions/menu-action';
import { Command } from 'cadenza/api-client/workbook-commands';
import { getOtherHitObjects } from 'cadenza/api-client/workbook-map-api';
import { getLayerSetting } from 'cadenza/map/context/workbook/layer-context-util';
import { getSelectableLayers } from 'cadenza/map/interactions/workbook-selection/selectable-layers';
import { extractSelectableLayerIdsFromUrl } from 'cadenza/map/interactions/workbook-selection/workbook-selection-utils';
import { MapProperty } from 'cadenza/map/map-property';
import { getDpi } from 'cadenza/map/unit-utils';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import type { VisualizationType } from 'cadenza/ui/visualization-type-label/visualization-type-label';
import { createVisualizationTypeLabel } from 'cadenza/ui/visualization-type-label/visualization-type-label';
import { isDataAcquisitionModalWithGeometryOpen } from 'cadenza/workbook/acquisition/data-acquisition-modal-with-geometry/data-acquistion-modal-with-geometry-util';
import { isMap, isViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import type { DrillThroughDto } from 'cadenza/workbook/drill-through/model/drill-through-dto';
import { isEditGeometryDialogOpen } from 'cadenza/workbook/edit-geometry-dialog/edit-geometry-dialog-util';
import { isDataViewLayer } from 'cadenza/workbook/map/workbook-layer-type-util';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key.gen';
import { createDrillThroughActions } from 'cadenza/workbook/selection-cart/drill-through-action';
import {
  adjustPositionWithinBounds,
  position,
  setupSelectionCartLayout,
} from 'cadenza/workbook/selection-cart/layout/layout';
import {
  createActionIconButtonRespectingMenuActions,
  getDataView,
  getSelectionMap,
  isDrillThroughDirect,
} from 'cadenza/workbook/selection-cart/selection-cart-utils';
import type { MapSelectionRequest } from 'cadenza/workbook/selection-cart/selection-cart.state';
import {
  clear,
  clearRelevantStateOnSelectionChange,
  externalLayerObjectInfoSingleExecution,
  fetchExternalLayerObjectInfo,
  fetchObjectInfo,
  getAdditionalMapClientSideHits,
  getLastMapSelectionRequest,
  getLastMaximizeSize,
  getLastPosition,
  getObjectInfoOptions,
  getSelectionClickPx,
  getSelectionView,
  getSelectionViewId,
  isDirty,
  isManuallyPositioned,
  isManuallyResized,
  isMinimized,
  isSelectionOnMap,
  markManuallyPositioned,
  markManuallyResized,
  objectInfoSingleExecution,
  setDirty,
  setLastPosition,
  setMinimized,
  setObjectInfoOptions,
  setOtherLayersLoadingState,
} from 'cadenza/workbook/selection-cart/selection-cart.state';
import type { SelectionInLayerDto } from 'cadenza/workbook/selection/model/selection-in-other-layers-dto';
import { isPanelVisible } from 'cadenza/workbook/state/workbook-panel.state';
import type { View } from 'cadenza/workbook/view/view';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getActiveViewId,
  getDetailViewId,
  getLayerDataByLayerId,
  getLayerName,
  getSelection,
  getSelectionExternalLayer,
  getSelectionInOtherLayers,
  getViewByDataViewId,
  getViewByLayerId,
  getWorkbookNameAndHash,
  hasSelectionOrExternalLayerSelection,
  isCombinationChart,
  isDataViewInCurrentWorksheet,
  isFeatureInfoEnabledForDataView,
  isInCombinationChart,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { activateView, setActiveLayerId, togglePanelVisibility } from 'cadenza/workbook/workbook.state';
import { isLinearLayout, worksheetLayout } from 'cadenza/workbook/worksheet-layout';

import './selection-cart.css';
import i18n from './selection-cart.properties';

const COMPONENT_NAME = 'd-selection-cart';
const DRAG_HANDLE_CLASS = `${COMPONENT_NAME}--drag-handle`;

// The number of drill through actions before they are pushed into a sub menu.
const SUBMENU_THRESHOLD = 5;

export class SelectionCart extends HTMLElement {
  // Do not throttle this because this needs to be executed asap for smooth animation.
  // Also, the condition within the function is falsy during resize anyway.
  readonly #infoContainerResizeObserver = new ResizeObserver(() => this.#adaptAutoHeightWithinBounds());
  readonly #windowIntersectionObserver = new IntersectionObserver(
    throttle(() => adjustPositionWithinBounds(this)),
    { threshold: 1 },
  );

  #lastFocusedElement?: HTMLElement;
  #mapEventTeardowns: Teardown[] = [];
  #teardowns: Teardown[] = [];
  #layoutTeardowns: Teardown[] = [];

  connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'd-stack-v', 'd-resizable');
    if (isLayoutingSupported()) {
      this.#layoutTeardowns.push(...setupSelectionCartLayout(this));
    }
    this.#teardowns.push(
      on(worksheetLayout, 'change', () => {
        if (isLayoutingSupported() && this.#layoutTeardowns.length === 0) {
          this.#layoutTeardowns.push(...setupSelectionCartLayout(this));
        } else {
          this.#layoutTeardowns = tearDown(this.#layoutTeardowns);
        }
      }),
    );
    this.#windowIntersectionObserver.observe(this);
    this.setAttribute('tabindex', '-1'); // needed for keyboard navigation (see this.#toggleHiddenAndFixFocus)
    this.#toggleHiddenAndFixFocus(true);
    this.#teardowns.push(
      observeStore(getStore(), isDirty, async (dirty) => {
        if (dirty) {
          // special handling for map
          this.#clearSelectionOnPrimaryInteractionChange();
          fetchNewDataOnSelectionChange();
          this.#setLastPosition();
          await this.#render();
          // clears certain state that are no longer relevant after a change in selection
          getStore().dispatch(
            clearRelevantStateOnSelectionChange({
              isSelectionAvailable: hasSelectionOrExternalLayerSelection(getStore().getState()),
              minimized: isMinimized(getStore().getState()),
              isMapSelected: isSelectionOnMap(getStore().getState()),
            }),
          );
        }
      }),
      this.#windowIntersectionObserver,
      this.#infoContainerResizeObserver,
      on(worksheetLayout, 'change', () => {
        this.#closeIfNecessaryForLinearLayout();
      }),
      observeStore(
        getStore(),
        (state) => getDetailViewId(state),
        () => {
          this.#closeIfNecessaryForLinearLayout();
        },
      ),
      on(getPopupParent(this), 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.#closeSelectionCart();
        }
      }),
    );
    this.#fixMouseWheelScrolling();
  }

  #toggleHiddenAndFixFocus(hidden: boolean) {
    this.hidden = hidden;

    if (hidden) {
      this.#lastFocusedElement?.focus();
    } else {
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        this.#lastFocusedElement = document.activeElement;
      }
      focusFirst(this);
    }
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
    this.#mapEventTeardowns = tearDown(this.#mapEventTeardowns);
    this.#layoutTeardowns = tearDown(this.#layoutTeardowns);
  }

  // eslint-disable-next-line no-restricted-syntax
  async #render() {
    const selection = getSelection(getStore().getState());
    const selectionRecords = selection?.records;
    const selectionAvailable = hasSelectionOrExternalLayerSelection(getStore().getState());
    const dataView = selectionRecords && getDataView(selectionRecords);
    const worksheetChanged = dataView && !isDataViewInCurrentWorksheet(getStore().getState(), dataView.id);
    if (!selectionAvailable || worksheetChanged) {
      this.#toggleHiddenAndFixFocus(true);
      render(nothing, this);
      queueMicrotask(() => getStore().dispatch(setDirty(false)));
      return;
    }

    const isMapSelected = isSelectionOnMap(getStore().getState());
    const minimized = isMinimized(getStore().getState());
    this.classList.toggle('is-minimized', minimized);
    const showDrillThroughDirectly =
      isDrillThroughDirect() &&
      selectionRecords &&
      !isDataAcquisitionModalWithGeometryOpen() &&
      !isEditGeometryDialogOpen();
    render(
      html`
        <div class="${COMPONENT_NAME}--header d-stack-v space-1">
          <div class="${COMPONENT_NAME}--header-actions d-stack-h space-1">
            <div class="${DRAG_HANDLE_CLASS} d-stack-h" title="${i18n('dragIndicatorTooltip')}">
              <div class="${COMPONENT_NAME}--title">${i18n('selectionTitle')}</div>
            </div>
            <div class="d-stack-h space-1">
              ${isMapSelected ?
                createIconButton(
                  icon(minimized ? maximizeIcon : minimizeIcon),
                  i18n(minimized ? 'maximizeTooltip' : 'minimizeTooltip'),
                  {
                    size: 'xs',
                    variant: 'borderless',
                    onclick: () => {
                      // If it was minimized before, we might need to fetch extra information
                      if (minimized && selectionAvailable) {
                        fetchNewData();
                      }
                      getStore().dispatch(setMinimized(!minimized));
                      this.#render();
                    },
                  },
                )
              : nothing}
              ${createIconButton(icon(closeIcon), i18n('closeIconTooltip'), {
                size: 'xs',
                variant: 'borderless',
                onclick: () => this.#closeSelectionCart(),
              })}
            </div>
          </div>
          <hr />
        </div>
        <div class="${COMPONENT_NAME}--scroll-container d-resizable--scroll-section d-stack-v space-2">
          <div
            ${ref((element) => {
              this.#infoContainerResizeObserver.disconnect();
              if (element) {
                this.#infoContainerResizeObserver.observe(element);
              }
            })}
            class="${COMPONENT_NAME}--info-container d-stack-v space-${minimized ? '2' : '3'}"
          >
            ${createAttributeSection()} ${await createActionSection()}
            ${!minimized && showDrillThroughDirectly ?
              this.#buildDrillThroughPanel(selection.drillThroughDestinations, selectionRecords.dataViewId, 'maximized')
            : nothing}
            ${!minimized ? await getMaximizedContent() : nothing}
          </div>
          ${minimized && showDrillThroughDirectly ?
            this.#buildDrillThroughPanel(selection.drillThroughDestinations, selectionRecords.dataViewId, '')
          : nothing}
          ${minimized ? await getMinimizedContent() : nothing}
        </div>
      `,
      this,
    );
    const lastMaximizeSize = getLastMaximizeSize(getStore().getState());
    if (minimized || this.#isNotManuallyResized) {
      // falls back to the one defined in css
      this.style.width = '';
      this.style.height = '';
      // When changed from minimized to maximize
    } else if (lastMaximizeSize && !this.#isNotManuallyResized) {
      const [width, height] = lastMaximizeSize;
      this.style.width = px(width);
      this.style.height = px(height);
    }
    // should not be hidden before positioning which needs bounding rect.
    this.#toggleHiddenAndFixFocus(false);
    // set the click position after minimize/maximize width and height is adjusted
    this.#setClickPosition();
    // Initial setup of position, and adjust within bounds, if necessary.
    // Can grow out of bounds when maximizing the cart that is at the bottom.
    adjustPositionWithinBounds(this);
    getStore().dispatch(setDirty(false));
  }

  #buildDrillThroughPanel(drillThroughDestinations: DrillThroughDto[], dataViewId: DataViewId, styleClass: string) {
    return html`<div class="${COMPONENT_NAME}--drill-through-panel d-menu ${styleClass}">
      ${renderDrillThroughs(
        createDrillThroughActions({
          drillThroughDestinations,
          dataViewId,
        }),
      )}
    </div>`;
  }

  #fixMouseWheelScrolling() {
    if (isSafari) {
      this.classList.add('safari');
    } else if (isChromiumBrowser) {
      this.classList.add('chromium');
    }
  }

  async #clearSelectionOnPrimaryInteractionChange() {
    this.#mapEventTeardowns = tearDown(this.#mapEventTeardowns);
    const isMapSelected = isSelectionOnMap(getStore().getState());
    if (isMapSelected) {
      const map = await getSelectionMap();
      if (map) {
        this.#mapEventTeardowns = tearDown(this.#mapEventTeardowns);
        this.#mapEventTeardowns.push(
          map.on(`change:${MapProperty.PRIMARY_INTERACTION}` as EventTypes, () => {
            const interaction = map.get(MapProperty.PRIMARY_INTERACTION);
            if (interaction.isWorkbookSelectionInteraction) {
              // When reloading a map with WorkbookSelectionInteraction as primaryInteraction the
              // primaryInteraction changes from undefined to an instance of WorkbookSelectionInteraction.
              // This would clear the selection.
              return;
            }
            this.#closeSelectionCart();
          }),
        );
      }
    }
  }

  #setClickPosition() {
    const positionPx = getSelectionClickPx(getStore().getState());
    // positionPx is present on every click, it is not present only on f5-refresh
    if (positionPx) {
      if (!isManuallyPositioned(getStore().getState())) {
        const [x, y] = positionPx;
        // First adapt the height if the cart is not manually resized yet.
        // Otherwise, the position logic will use the last size which might not be relevant for new dynamic content.
        this.#adaptAutoHeightWithinBounds();
        position(x, y, this);
      }
    }
  }

  /**
   * Set last saved position if there is no click position during f5 (refresh)
   */
  #setLastPosition() {
    const lastPosition = getLastPosition(getStore().getState());
    if (lastPosition && !getSelectionClickPx(getStore().getState())) {
      Object.assign(this.style, lastPosition);
    }
  }

  #closeIfNecessaryForLinearLayout() {
    if (isLinearLayout() && !getDetailViewId(getStore().getState())) {
      this.#closeSelectionCart();
    }
  }

  #closeSelectionCart() {
    this.#toggleHiddenAndFixFocus(true);
    render(nothing, this);
    if (hasSelectionOrExternalLayerSelection(getStore().getState())) {
      getStore().dispatch(executeCommand(Command.SET_SELECTION({})));
    }
    getStore().dispatch(clear());
    objectInfoSingleExecution.cancelCurrent();
    externalLayerObjectInfoSingleExecution.cancelCurrent();
    this.#mapEventTeardowns = tearDown(this.#mapEventTeardowns);
  }

  get dragHandleClass() {
    return DRAG_HANDLE_CLASS;
  }

  markManualResize() {
    if (this.#isNotManuallyResized) {
      getStore().dispatch(markManuallyResized());
    }
  }

  markManualPosition() {
    if (!isManuallyPositioned(getStore().getState())) {
      getStore().dispatch(markManuallyPositioned());
    }
  }

  storeLastPosition() {
    const { top, right, bottom, left, width, height } = this.style;
    getStore().dispatch(
      setLastPosition({
        position: {
          top,
          right,
          bottom,
          left,
          width,
          height,
        },
        updateLastMaximizeSize: !isMinimized(getStore().getState()),
      }),
    );
  }

  get #isNotManuallyResized() {
    return !isManuallyResized(getStore().getState());
  }

  #adaptAutoHeightWithinBounds() {
    // After async rendering of certain sections like object-info, the height of cart needs to adjusted,
    // but only if user has not yet manually resized the cart, or if it is in minimized mode (which can also have async rendering)
    // Height should be extended at the bottom to the available space.
    if (this.#isNotManuallyResized || isMinimized(getStore().getState())) {
      // force the height to increase to fit-content, and later clamp it within the bounds, if necessary
      this.style.height = 'fit-content';
      adjustPositionWithinBounds(this);
    }
  }
}

customElements.define(COMPONENT_NAME, SelectionCart);

function createAttributeSection() {
  return html`<div class="d-stack-v space-0_5">
    <div class="bold">${createAttributeElement()}</div>
    <div class="ellipsis" role="status">${getSelectedValuesInfo()}</div>
  </div>`;
}

function getSelectedValuesInfo() {
  const selectionExternalLayer = getSelectionExternalLayer(getStore().getState());
  if (selectionExternalLayer) {
    return i18n('objectsSelected', { count: selectionExternalLayer.numberOfSelectedRecords });
  }
  const selection = getSelection(getStore().getState())!;
  const records = requireNonNullable(selection.records);
  const count = records.values.length;
  const dataView = getDataView(records);
  if (isMap(dataView.visualizationKey)) {
    return i18n('objectsSelected', { count });
  } else {
    const isTable = isViewType(dataView.visualizationKey, ViewType.TABLE);
    let definingDimensionIndex = 0;
    if (isTable) {
      const { selectionElement, definingDimensions } = records;
      definingDimensionIndex = definingDimensions.findIndex((definingDimension) =>
        isEqual(definingDimension, selectionElement),
      );
    }
    // If the definingDimensionIndex is equal to -1 we're working with the measure,
    // otherwise definingDimensionIndex is used to show the exact value of the selected cell.
    return definingDimensionIndex === -1 ?
        i18n('measureSelected', { count, selectionObjectName: selection.selectionObjectName })
      : i18n('valuesSelected', {
          count,
          selectionObjectName: selection.selectionObjectName,
          value: records.formattedValues?.at(0)?.at(definingDimensionIndex),
        });
  }
}

function createAttributeElement() {
  const selection = getSelection(getStore().getState());
  const selectionExternalLayer = getSelectionExternalLayer(getStore().getState());
  if (selection?.records) {
    const dataView = getDataView(selection.records);
    const label =
      isInCombinationChart(getStore().getState(), dataView.id) || isMap(dataView.visualizationKey) ?
        dataView.name
      : getViewByDataViewId(getStore().getState(), dataView.id).name;
    return getLinkedVisualizationTypeLabel(dataView.visualizationKey, label, dataView.id);
  } else if (selectionExternalLayer) {
    const viewId = getViewByLayerId(getStore().getState(), selectionExternalLayer.layerId).id;
    const layerName = getLayerName(getStore().getState(), viewId, selectionExternalLayer.layerId);
    return getLinkedVisualizationTypeLabel('EXTERNAL_LAYER', layerName, selectionExternalLayer.layerId);
  }
}

function getLinkedVisualizationTypeLabel(type: VisualizationType, visualizationName: string, layerId: LayerId) {
  const isDesignerAvailable = isWorkbookFeatureAvailable(getStore().getState(), 'workbook-design');
  let label: HTMLElement = createVisualizationTypeLabel(type, visualizationName);
  if (isDesignerAvailable) {
    const viewId = (
      getViewByLayerId(getStore().getState(), layerId) ??
      getViewByDataViewId(getStore().getState(), layerId as DataViewId)
    ).id;
    label = createAnchorButton(label, {
      onclick: async () => {
        let displayLayerSettingsFunc;
        if (isMap(type)) {
          const { getDisplayLayerSettingsAction } =
            await import('cadenza/workbook/workbook-panel/settings/map/get-display-layer-settings-action');
          const viewElement = requireNonNullable(
            document.querySelector<View>(`.d-worksheet-view[data-id="${viewId}"]`),
          );
          displayLayerSettingsFunc = getDisplayLayerSettingsAction(viewId, viewElement, layerId);
        } else if (isCombinationChart(getStore().getState(), viewId)) {
          displayLayerSettingsFunc = () => activateCombinationChartLayer(layerId);
        } else {
          displayLayerSettingsFunc = showDesigner;
        }
        if (displayLayerSettingsFunc) {
          if (getActiveViewId(getStore().getState()) !== viewId) {
            getStore().dispatch(activateView(viewId));
            // unfortunately, the designer panel rendering is async because it loads the respective settings-panel in async,
            // so, we should wait 1 cycle to display layer settings when activeView was changed.
            setTimeout(() => displayLayerSettingsFunc());
          } else {
            displayLayerSettingsFunc();
          }
        }
      },
    });
    label.classList.add('d-anchor-light');
  }
  return label;
}

/**
 * Fetch the new data because of change in selection
 */
function fetchNewDataOnSelectionChange() {
  const minimized = isMinimized(getStore().getState());
  const maximized = !minimized;
  const hasSelection = hasSelectionOrExternalLayerSelection(getStore().getState());
  const hasExternalLayerSelection = Boolean(getSelectionExternalLayer(getStore().getState()));
  const emptyObjectInfo = !getObjectInfoOptions(getStore().getState()).objectInfo;
  // if maximized and there is selection, fetches new data
  if (maximized && hasSelection) {
    fetchNewData(true);
  } else if (minimized && hasExternalLayerSelection && emptyObjectInfo) {
    // object info is needed to highlight selected features for external layer selection in minimized state
    dispatchFetchObjectInfo();
  }
}

function fetchNewData(forceFetchObjectInfo = false) {
  const emptyObjectInfo = !getObjectInfoOptions(getStore().getState()).objectInfo;
  if (forceFetchObjectInfo || emptyObjectInfo) {
    dispatchFetchObjectInfo();
  }
  const emptyResultsInOtherLayers = !getSelectionInOtherLayers(getStore().getState())?.selectionInLayers.length;
  const selectionRequest = getLastMapSelectionRequest(getStore().getState());
  if (emptyResultsInOtherLayers && selectionRequest && hasApplicableOtherLayers(selectionRequest.geometry)) {
    populateOtherLayersSelection(selectionRequest);
  } else {
    queueMicrotask(() => getStore().dispatch(setOtherLayersLoadingState('LOADING_COMPLETE')));
  }
}

function hasApplicableOtherLayers(selectionGeometry: Geometry) {
  const view = requireNonNullable(getSelectionView(getStore().getState()));
  const hasApplicableLayer = (node: WorkbookLayerTreeNode) => {
    const layer = view.layerDefinitions[node.id];
    const isLayerVisible = getSetting<boolean>(layer, LayerSettingsKey.toPersistenceKey('VISIBLE'));
    // skip invisible and currently selected layer
    if (isLayerVisible && layer.id !== getSelectionLayerId()) {
      switch (layer.structureType) {
        case 'SINGLE':
          return isApplicableDataViewLayer(layer) || isApplicableExternalLayer(selectionGeometry, layer);
        case 'GROUP':
          return node.children.some(hasApplicableLayer);
        case 'TOGGLE':
          return node.children.some(hasApplicableLayer);
      }
    }
  };
  // need to loop through the tree because inorder to determine visibility of single-layer, it's parent's visibility must be checked.
  return view.layerTree.some(hasApplicableLayer);
}

function getSelectionLayerId() {
  return (
    getSelection(getStore().getState())?.records?.dataViewId ??
    getSelectionExternalLayer(getStore().getState())?.layerId
  );
}

function isApplicableDataViewLayer(layer: WorkbookLayerDto) {
  return isDataViewLayer(layer) && isFeatureInfoEnabledForDataView(getStore().getState(), layer.id as DataViewId);
}

function isApplicableExternalLayer(geometry: Geometry, layer: WorkbookLayerDto) {
  // Only point selection is supported
  return (
    geometry.type === 'Point' &&
    getLayerSetting<boolean>(
      requireNonNullable(getLayerDataByLayerId(getStore().getState(), layer.id)).definition,
      'EXTERNAL_LAYER_FEATURE_INFO',
    )
  );
}

async function dispatchFetchObjectInfo() {
  const map = requireNonNullable(await getSelectionMap());
  const mapSrs = map.getView().getProjection().getCode();
  const dpi = getDpi(map.get(MapProperty.HIDPI));
  const mapResolution = requireNonNullable(map.getView().getResolution());
  const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  const objectInfoEnabled = getSelection(getStore().getState())?.featureInfoEnabled;
  const selectionExternalLayer = getSelectionExternalLayer(getStore().getState());
  const externalObjectInfoEnabled =
    selectionExternalLayer &&
    getLayerSetting(
      requireNonNullable(getLayerDataByLayerId(getStore().getState(), selectionExternalLayer.layerId)).definition,
      'EXTERNAL_LAYER_FEATURE_INFO',
    );
  if (externalObjectInfoEnabled) {
    const currentObjectInfoLayerId = getObjectInfoOptions(getStore().getState())?.objectInfo?.layerId;
    const currentSelectionLayerId = selectionExternalLayer.layerId;
    // object info for external layer is retrieved and set during the selection interaction
    // so we do not need to retrieve it here again, if object info for the same layerId is already set
    if (currentObjectInfoLayerId === currentSelectionLayerId) {
      return;
    }
    const selectionRequest = getLastMapSelectionRequest(getStore().getState())!;
    const viewId = getSelectionViewId(getStore().getState())!;
    getStore().dispatch(
      fetchExternalLayerObjectInfo({
        externalLayerObjectInfoRequest: {
          workbookNameAndHash,
          viewId,
          layerId: currentSelectionLayerId,
          srsCode: mapSrs,
          dpi,
          mapSize: selectionRequest.mapSize,
          geometry: selectionRequest.geometry,
          mapExtent: selectionRequest.mapExtent,
        },
      }),
    );
  } else if (objectInfoEnabled) {
    getStore().dispatch(
      fetchObjectInfo({
        workbookNameAndHash,
        mapSrs,
        mapResolution,
        layerId: requireNonNullable(getSelection(getStore().getState())?.records).dataViewId,
      }),
    );
  } else {
    // clear old object-info as selection is changed to the layer that has object-info disabled
    getStore().dispatch(setObjectInfoOptions());
  }
}

const getOtherHitObjectsExecution = backendSingleExecution(getOtherHitObjects);

/**
 * Collects selection hits from layers other than the primary selection layer and updates the selection state.
 *
 * This function combines client-side hits already stored in Redux with server-side hits
 * fetched from the API, then dispatches a command to store these additional selections.
 * This enables the "selection in other layers" feature in the UI.
 *
 * @param lastSelectionRequest - The original map selection request information
 */
async function populateOtherLayersSelection(lastSelectionRequest: MapSelectionRequest) {
  getStore().dispatch(setOtherLayersLoadingState('LOADING'));

  const map = requireNonNullable(await getSelectionMap());
  const srsCode = map.getView().getProjection().getCode();
  const dpi = getDpi(map.get(MapProperty.HIDPI));
  const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  const viewId = requireNonNullable(getSelectionViewId(getStore().getState()));

  const clientSideSelectionInOtherDataViews = getAdditionalMapClientSideHits(getStore().getState());

  const selectableLayerIds = extractSelectableLayerIdsFromUrl(viewId);
  const selectableServerSideLayerIds = getSelectableLayers(viewId, selectableLayerIds)
    .filter((layer) => layer.selectionType === 'server')
    .map((layer) => layer.id);

  const selectionRequest = {
    ...lastSelectionRequest,
    selectableLayers: selectableServerSideLayerIds,
    workbookNameAndHash,
    viewId,
    srsCode,
    dpi,
  };

  try {
    const { selectionInOtherDataViews: serverSideSelectionInOtherDataViews, selectionInOtherExternalLayers } =
      await getServerSideHits(selectionRequest);

    await getStore()
      .dispatch(
        executeCommand(
          Command.SET_SELECTION_IN_OTHER_LAYERS({
            selectionInOtherDataViews: [...clientSideSelectionInOtherDataViews, ...serverSideSelectionInOtherDataViews],
            selectionInOtherExternalLayers,
          }),
        ),
      )
      .unwrap();
  } finally {
    getStore().dispatch(setOtherLayersLoadingState('LOADING_COMPLETE'));
  }
}

/**
 * Get the server-side hits from layers other than the primary selection layer.
 * If there is no server-side layer, then no server request is fired.
 */
async function getServerSideHits(selectionRequest: MapSelectionRequest) {
  if (!selectionRequest.selectableLayers?.length) {
    return {
      selectionInOtherDataViews: [],
      selectionInOtherExternalLayers: [],
    };
  }

  const hitObjectsInOtherLayers = await getOtherHitObjectsExecution(selectionRequest);

  const selectionInOtherDataViews = hitObjectsInOtherLayers
    .filter((hitObjects) => hitObjects.selectionRecords)
    .map((hitObjects) => hitObjects.selectionRecords!);

  const selectionInOtherExternalLayers: SelectionInLayerDto[] = hitObjectsInOtherLayers
    .filter((hitObjects) => hitObjects.externalLayerObjectInfo)
    .map((hitObjects) => {
      return {
        layerId: hitObjects.externalLayerObjectInfo!.layerId,
        numberOfSelectedRecords: hitObjects.externalLayerObjectInfo!.objectInfoValues.length,
      };
    });

  return { selectionInOtherDataViews, selectionInOtherExternalLayers };
}

async function getMaximizedContent() {
  const { getMaximizeContent } = await import('cadenza/workbook/selection-cart/selection-cart-content-provider');
  return getMaximizeContent();
}

async function getMinimizedContent() {
  if (isSelectionOnMap(getStore().getState())) {
    const { getHighlightFeature } = await import('cadenza/workbook/selection-cart/selection-cart-content-provider');
    return getHighlightFeature();
  } else {
    const selection = getSelection(getStore().getState());
    if (
      selection?.records?.dataViewId &&
      isInCombinationChart(getStore().getState(), selection.records.dataViewId) &&
      getSelectionInOtherLayers(getStore().getState())?.selectionInLayers.length
    ) {
      const { getOtherLayers } = await import('cadenza/workbook/selection-cart/selection-cart-content-provider');
      // For combination chart, mark the other layers loading state as complete because they are always filled when selection is set.
      // This location is chosen as a central place instead of setting LOADING_COMPLETE from multiple different place.
      getStore().dispatch(setOtherLayersLoadingState('LOADING_COMPLETE'));
      return html`<div class="${COMPONENT_NAME}--info-container d-stack-v space-2">
        <hr />
        ${getOtherLayers()}
      </div>`;
    }
    return nothing;
  }
}

async function createActionSection() {
  const { getActions } = await import('cadenza/workbook/selection-cart/selection-cart-action-provider');
  const actions = await getActions();
  return html`<div class="${COMPONENT_NAME}--actions d-stack-h space-1">
    ${actions.map((action) => createActionIconButtonRespectingMenuActions(action, true))}
  </div>`;
}

function renderDrillThroughs(action?: MenuAction) {
  const content = action?.actionProvider() || [];
  if (!action || !content.length) {
    return;
  }

  const { panelActions, subMenuActions } = extractPanelAndSubMenuActions(content);
  const panelItems = unpackMenuItemGroups(panelActions, true);
  const panelContent = panelItems.flatMap((item, i, items) => {
    return normalizeContent(createMenuContent(item, i, items));
  });
  if (!subMenuActions.length) {
    return [html`<hr />`, ...panelContent];
  }

  const subMenuItems = unpackMenuItemGroups(subMenuActions, true);
  const submenu: MenuItem[] = [
    {
      label: i18n('moreDrillThroughs'),
      subMenu: subMenuItems,
    },
  ];
  const subMenuContent = submenu.flatMap((item, i, items) => {
    return normalizeContent(createMenuContent(item, i, items));
  });
  if (moreDrillThroughsButtonNeedsSeparator(panelItems, subMenuItems)) {
    subMenuContent.unshift(document.createElement('hr'));
  }
  return [html`<hr />`, ...panelContent, subMenuContent];
}

/**
 * Extracts a list of Actions or MenuItemGroups into two different lists so that one list (panelActions) can be displayed
 * directly on the select cart panel and the other list (subMenuActions) can be put in to a submenu.
 *
 * @param content - List of Actions or MenuItemGroups to extract.
 */
function extractPanelAndSubMenuActions(content: (Action | MenuItemGroup)[]) {
  const panelActions: (Action | MenuItemGroup)[] = [];
  const subMenuActions: (Action | MenuItemGroup)[] = [];
  let panelActionsCount = 0;
  for (const item of content) {
    // The threshold has already been reached: just put the item to the subMenuActions.
    if (panelActionsCount >= SUBMENU_THRESHOLD) {
      subMenuActions.push(item);
      continue;
    }
    // Threshold is not yet reached, if the item is not a group put it to panelActions.
    if (!isMenuItemGroup(item)) {
      panelActions.push(item);
      panelActionsCount++;
      // If the item is a group and adding its group items does not surpass the threshold.
    } else if (panelActionsCount + item.group.length <= SUBMENU_THRESHOLD) {
      panelActions.push(item);
      panelActionsCount += item.group.length;
      // The item is a group and adding its groups items surpasses the threshold.
      // This branch will be entered once at most.
    } else {
      let group = [];
      let subMenuGroup = [];
      for (const element of item.group) {
        if (panelActionsCount < SUBMENU_THRESHOLD) {
          group.push(element);
          panelActionsCount++;
        } else {
          subMenuGroup.push(element);
        }
      }
      panelActions.push({
        label: item.label,
        group,
      });
      // UX needs the group header also in the sub menu.
      subMenuActions.push({
        label: item.label,
        group: subMenuGroup,
      });
    }
  }
  return { panelActions, subMenuActions };
}

/**
 * UX needs a horizontal ruler upfront the sub menu button if the items in the submenu start with a new group.
 */
function moreDrillThroughsButtonNeedsSeparator(
  panelItems: (Action | MenuItemGroup)[],
  subMenuItems: (Action | MenuItemGroup)[],
) {
  const lastPanelMenuGroup =
    panelItems.length && isMenuItemGroup(panelItems[panelItems.length - 1]) ?
      (panelItems[panelItems.length - 1] as MenuItemGroup)
    : undefined;
  const firstSubMenuGroup = subMenuItems.length && isMenuItemGroup(subMenuItems[0]) ? subMenuItems[0] : undefined;
  return lastPanelMenuGroup && firstSubMenuGroup && lastPanelMenuGroup.label !== firstSubMenuGroup.label;
}

function showDesigner() {
  getStore().dispatch(togglePanelVisibility(WorkbookPanelName.VIEW_SETTINGS));
}

function activateCombinationChartLayer(layerId: LayerId) {
  if (!isPanelVisible(getStore().getState(), WorkbookPanelName.VIEW_SETTINGS)) {
    getStore().dispatch(togglePanelVisibility(WorkbookPanelName.VIEW_SETTINGS));
    // Give some time for designer panel to render
    setTimeout(() => getStore().dispatch(setActiveLayerId(layerId)));
  } else {
    getStore().dispatch(setActiveLayerId(layerId));
  }
}

function isLayoutingSupported() {
  return !(supportsTouch && isLinearLayout());
}
